import { useState, useEffect } from "react";
import { supabase } from "@/lib/supabase";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search, Edit, Trash, Plus } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/context/AuthContext";
import { TourPackageItem } from "@/types/database";
import { useNavigate } from "react-router-dom";

// Format number to IDR currency
function formatIDR(amount: number) {
  return new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

export default function TourPackage() {
  const [searchQuery, setSearchQuery] = useState("");
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [packageToDelete, setPackageToDelete] = useState<TourPackageItem | null>(null);
  const navigate = useNavigate();

  const queryClient = useQueryClient();

  // Fetch tour packages with real-time subscription
  const { data: tourPackages = [], isLoading } = useQuery({
    queryKey: ["tour_packages"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("tour_package")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) {
        toast.error(error.message);
        throw error;
      }
      return data as TourPackageItem[];
    },
  });

  // Subscribe to tour changes
  useEffect(() => {
    const channel = supabase
      .channel('tour_changes')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'tours'
        },
        (payload) => {
          // Invalidate both tours and tour packages queries to refresh the data
          queryClient.invalidateQueries({ queryKey: ["tours"] });
          queryClient.invalidateQueries({ queryKey: ["tour_packages"] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [queryClient]);

  // Delete tour package mutation
  const deleteTourPackageMutation = useMutation({
    mutationFn: async (id: number) => {
      const { error } = await supabase
        .from("tour_package")
        .delete()
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tour_packages"] });
      toast.success("Tour package deleted successfully!");
      setIsDeleteDialogOpen(false);
      setPackageToDelete(null);
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const handleDelete = (tourPackage: TourPackageItem) => {
    setPackageToDelete(tourPackage);
    setIsDeleteDialogOpen(true);
  };

  const filteredPackages = tourPackages.filter((pkg) => {
    const searchLower = searchQuery.toLowerCase();
    return pkg.tour_package_name?.toLowerCase().includes(searchLower);
  });

  return (
    <div className="flex flex-col gap-4 py-4 px-4">
      <header className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="space-y-1">
          <h1 className="text-3xl font-bold tracking-tight">Tour Packages</h1>
          <p className="text-muted-foreground">Create and manage your tour packages here.</p>
        </div>
        <Button onClick={() => navigate("/tourpackage/new")}>
          <Plus className="mr-2 h-4 w-4" />
          Add Package
        </Button>
      </header>

      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-4 mb-4">
            <Search className="h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search packages by name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="max-w-sm"
            />
          </div>

          <div className="relative">
            {isLoading ? (
              <div className="absolute inset-0 flex items-center justify-center bg-background/50">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary/30 border-t-primary" />
              </div>
            ) : (
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Package Name</TableHead>
                      <TableHead>Transport</TableHead>
                      <TableHead className="w-[100px] text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredPackages.map((pkg) => (
                      <TableRow key={pkg.id}>
                        <TableCell>{pkg.tour_package_name}</TableCell>
                        <TableCell>
                          {[
                            pkg.transport_kuta && `Kuta: ${formatIDR(pkg.transport_kuta)}`,
                            pkg.transport_nusadua && `Nusa Dua: ${formatIDR(pkg.transport_nusadua)}`,
                            pkg.transport_ubud && `Ubud: ${formatIDR(pkg.transport_ubud)}`,
                            pkg.transport_other && `Other: ${formatIDR(pkg.transport_other)}`
                          ].filter(Boolean).join(", ")}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => navigate(`/tourpackage/${pkg.id}`)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(pkg)}
                          >
                            <Trash className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the tour
              package.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setPackageToDelete(null)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (packageToDelete) {
                  deleteTourPackageMutation.mutate(packageToDelete.id);
                }
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
